import React from 'react';
import { createBrowserRouter, RouterProvider, ScrollRestoration, Outlet } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Container, GlobalStyles } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import theme from './theme';
import Header from './components/Header';
import Footer from './components/Footer';
import { UserProvider } from './contexts/UserContext';
import HomePage from './pages/HomePage';
import GameDetailsPage from './pages/GameDetailsPage';
import SearchPage from './pages/SearchPage';
import PlatformListPage from './pages/categories/PlatformListPage';
import GenreListPage from './pages/categories/GenreListPage';
import GameListByCategory from './pages/categories/GameListByCategory';
import GameEngineListPage from './pages/categories/GameEngineListPage';
import ThemeListPage from './pages/categories/ThemeListPage';
import { GoogleOAuthProvider } from '@react-oauth/google';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import ProfileEditPage from './pages/ProfileEditPage'; // この行を追加
import PublicProfilePage from './pages/PublicProfilePage';
import GameReviewsPage from './pages/GameReviewsPage';
import ProfileSettingsPage from './pages/ProfileSettingsPage';
import TrendingNewReleasesPage from './pages/gamelist/TrendingNewReleasesPage';
import PopularGamesByYearPage from './pages/PopularGamesByYearPage';
import { HelmetProvider } from 'react-helmet-async';
import PrivacyPolicyPage from './pages/policies/PrivacyPolicyPage';
import TermsOfServicePage from './pages/policies/TermsOfServicePage';
import AboutPage from './pages/AboutPage';
import WhyRecordPage from './pages/WhyRecordPage';
import GameStoryPage from './pages/contents/story/GameStoryPage';
import CreateGameStoryPage from './pages/contents/story/CreateGameStoryPage';
import EditGameStoryPage from './pages/contents/story/EditGameStoryPage';
import GameStoryDetailPage from './pages/contents/story/GameStoryDetailPage';
import AllGameStoriesPage from './pages/contents/story/AllGameStoriesPage';
import GameImagesPage from './pages/GameImagesPage';
import MaintenancePage from './pages/MaintenancePage';
import { maintenanceConfig } from './config/maintenance';
import GameListDetailPage from './pages/GameListDetailPage';
import EditGameListPage from './pages/EditGameListPage';
import GamePhraseDetailPage from './pages/contents/phrase/GamePhraseDetailPage';
import GamePhraseEditPage from './pages/contents/phrase/GamePhraseEditPage';
import GameTriviaDetailPage from './pages/contents/trivia/GameTriviaDetailPage';
import GameTriviaEditPage from './pages/contents/trivia/GameTriviaEditPage';
import GameReviewDetailPage from './pages/GameReviewDetailPage';
import GameReviewDetailPage_v2 from './pages/GameReviewDetailPage_v2';
import MemoryDetailPage from './pages/MemoryDetailPage';
import ReleaseCalendarPage from './pages/ReleaseCalendarPage';
import MobileBottomNav from './components/MobileBottomNav';
import { isPwa } from './utils/isPwa';
import InstallPwaPage from './pages/InstallPwaPage';
import DiscordCallback from './components/auth/DiscordCallback';
import XboxCallback from './components/auth/XboxCallback';
import PopularGamesByYearStatusPage from './pages/PopularGamesByYearStatusPage';
import AnticipatedGamesPage from './pages/gamelist/AnticipatedGamesPage';
import ReleaseNotesPage from './pages/ReleaseNotesPage';
import GamePlayLogPage from './pages/GamePlayLogPage';
import SteamCallback from './pages/auth/SteamCallback';
import EventPage from './pages/EventPage';
import WeeklyReleaseCalendarPage from './pages/WeeklyReleaseCalendarPage';
import UserGotyPage from './pages/UserGotyPage';
import EditGotyPage from './pages/EditGotyPage';
import ActivityDetailPage from './pages/ActivityDetailPage';
import PublicTimelinePage from './pages/PublicTimelinePage';
import AnimeDetailsPage from './pages/AnimeDetailsPage';

// React Query クライアントの設定
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 3, // 新鮮とみなす時間
      gcTime: 1000 * 60 * 5, // 5分間キャッシュを保持
      refetchOnWindowFocus: false, // ウィンドウフォーカス時の自動再取得を無効化
    },
  },
});

const globalStyles = (
  <GlobalStyles
    styles={{
      body: {
        overflowX: 'hidden',
      },
    }}
  />
);

const StandardLayout = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ 
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  }}>
    <Header />
    <Box 
      sx={{ 
        paddingTop: { xs: '56px', sm: '64px' },
        paddingBottom: { xs: isPwa() ? '80px' : 0, sm: isPwa() ? '80px' : 0 },
        flex: 1
      }}
    >
      <Container maxWidth="lg" sx={{ px: 1.5, py: 4 }}>
        {children}
      </Container>
    </Box>
    {isPwa() && <MobileBottomNav />}
    {!isPwa() && <Footer />}
  </Box>
);

const HomeLayout = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ 
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  }}>
    <Header />
    <Box sx={{ 
      paddingBottom: { xs: isPwa() ? '80px' : 0, sm: isPwa() ? '80px' : 0 },
      flex: 1
    }}>
      {children}
    </Box>
    {isPwa() && <MobileBottomNav />}
    {!isPwa() && <Footer />}
  </Box>
);

const FreeLayout = ({ children, hideFooter }: { children: React.ReactNode; hideFooter?: boolean }) => (
  <Box sx={{
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column'
  }}>
    <Header />
    <Box sx={{
      paddingTop: { xs: '56px', sm: '64px' },
      paddingBottom: { xs: isPwa() ? '80px' : 0, sm: isPwa() ? '80px' : 0 },
      flex: 1
    }}>
      {children}
    </Box>
    {isPwa() && <MobileBottomNav />}
    {!isPwa() && !hideFooter && <Footer />}
  </Box>
);

const router = createBrowserRouter([
  {
    element: (
      <>
        <ScrollRestoration />
        <Outlet />
      </>
    ),
    children: [
      {
        path: '/',
        element: <HomeLayout><HomePage /></HomeLayout>,
      },
      {
        path: '/game/:game_id',
        element: <HomeLayout><GameDetailsPage /></HomeLayout>,
      },
      {
        path: '/anime/:mal_id',
        element: <HomeLayout><AnimeDetailsPage /></HomeLayout>,
      },
      {
        path: '/search',
        element: <StandardLayout><SearchPage /></StandardLayout>,
      },
      {
        path: '/platforms',
        element: <StandardLayout><PlatformListPage /></StandardLayout>,
      },
      {
        path: '/genres',
        element: <StandardLayout><GenreListPage /></StandardLayout>,
      },
      {
        path: '/games/:type/:id',
        element: <StandardLayout><GameListByCategory /></StandardLayout>,
      },
      {
        path: '/game-engines',
        element: <StandardLayout><GameEngineListPage /></StandardLayout>,
      },
      {
        path: '/themes',
        element: <StandardLayout><ThemeListPage /></StandardLayout>,
      },
      {
        path: '/login',
        element: <FreeLayout><LoginPage /></FreeLayout>,
      },
      {
        path: '/profile',
        element: <StandardLayout><ProfilePage /></StandardLayout>,
      },
      {
        path: '/profile/settings',
        element: <StandardLayout><ProfileSettingsPage /></StandardLayout>,
      },
      {
        path: '/profile/:username',
        element: <StandardLayout><PublicProfilePage /></StandardLayout>,
      },
      {
        path: '/goty/:year/:username',
        element: <HomeLayout><UserGotyPage /></HomeLayout>,
      },
      {
        path: '/mygoty/:year/:username',
        element: <HomeLayout><UserGotyPage /></HomeLayout>,
      },
      {
        path: '/goty/:year/edit',
        element: <StandardLayout><EditGotyPage /></StandardLayout>,
      },
      {
        path: '/mygoty/:year/edit',
        element: <StandardLayout><EditGotyPage /></StandardLayout>,
      },
      {
        path: '/game/:gameId/reviews',
        element: <StandardLayout><GameReviewsPage /></StandardLayout>,
      },
      {
        path: '/trending-new-releases',
        element: <StandardLayout><TrendingNewReleasesPage /></StandardLayout>,
      },
      {
        path: '/popular-games/:year',
        element: <StandardLayout><PopularGamesByYearPage /></StandardLayout>,
      },
      {
        path: '/popular-games-status/:year',
        element: <FreeLayout><PopularGamesByYearStatusPage /></FreeLayout>,
      },
      {
        path: '/privacy-policy',
        element: <StandardLayout><PrivacyPolicyPage /></StandardLayout>,
      },
      {
        path: '/terms-of-service',
        element: <StandardLayout><TermsOfServicePage /></StandardLayout>,
      },
      {
        path: '/about',
        element: <StandardLayout><AboutPage /></StandardLayout>,
      },
      {
        path: '/why-record',
        element: <StandardLayout><WhyRecordPage /></StandardLayout>,
      },
      {
        path: '/game/:gameId/stories',
        element: <StandardLayout><GameStoryPage /></StandardLayout>,
      },
      {
        path: '/game/:gameId/stories/create',
        element: <StandardLayout><CreateGameStoryPage /></StandardLayout>,
      },
      {
        path: '/game/:gameId/stories/:storyId/edit',
        element: <StandardLayout><EditGameStoryPage /></StandardLayout>,
      },
      {
        path: '/game/:gameId/stories/:storyId',
        element: <FreeLayout><GameStoryDetailPage /></FreeLayout>,
      },
      {
        path: '/stories',
        element: <StandardLayout><AllGameStoriesPage /></StandardLayout>,
      },
      {
        path: '/game/:game_id/images',
        element: <FreeLayout><GameImagesPage /></FreeLayout>,
      },
      {
        path: '/maintenance',
        element: <FreeLayout><MaintenancePage /></FreeLayout>,
      },
      {
        path: '/list/:id',
        element: <StandardLayout><GameListDetailPage /></StandardLayout>,
      },
      {
        path: '/list/:listId/edit',
        element: <FreeLayout hideFooter><EditGameListPage /></FreeLayout>,
      },
      {
        path: '/phrase/:phraseId',
        element: <FreeLayout><GamePhraseDetailPage /></FreeLayout>,
      },
      {
        path: '/phrase/:phraseId/edit',
        element: <FreeLayout><GamePhraseEditPage /></FreeLayout>,
      },
      {
        path: '/trivia/:triviaId',
        element: <FreeLayout><GameTriviaDetailPage /></FreeLayout>,
      },
      {
        path: '/trivia/:triviaId/edit',
        element: <FreeLayout><GameTriviaEditPage /></FreeLayout>,
      },
      {
        path: '/game/:gameId/reviews/:reviewId',
        element: <FreeLayout><GameReviewDetailPage /></FreeLayout>,
      },
      {
        path: '/game/:gameId/reviews/:reviewId/v2',
        element: <FreeLayout><GameReviewDetailPage_v2 /></FreeLayout>,
      },
      {
        path: '/game/:gameId/memories/:memoryId',
        element: <FreeLayout><MemoryDetailPage /></FreeLayout>,
      },
      {
        path: '/release-calendar',
        element: <StandardLayout><ReleaseCalendarPage /></StandardLayout>,
      },
      {
        path: '/weekly-release-calendar',
        element: <StandardLayout><WeeklyReleaseCalendarPage /></StandardLayout>,
      },
      {
        path: '/install-pwa',
        element: <StandardLayout><InstallPwaPage /></StandardLayout>,
      },
      {
        path: '/auth/discord/callback',
        element: <DiscordCallback />,
      },
      {
        path: '/auth/xbox/callback',
        element: <XboxCallback />,
      },
      {
        path: '/anticipated-games',
        element: <StandardLayout><AnticipatedGamesPage /></StandardLayout>,
      },
      {
        path: '/release-notes',
        element: <FreeLayout><ReleaseNotesPage /></FreeLayout>,
      },
      {
        path: '/game/:gameId/playlog/:username?',
        element: <StandardLayout><GamePlayLogPage /></StandardLayout>,
      },
      {
        path: '/auth/steam/callback',
        element: <FreeLayout><SteamCallback /></FreeLayout>,
      },
      {
        path: '/event/:eventId',
        element: <FreeLayout><EventPage /></FreeLayout>,
      },
      {
        path: '/activity/:userId/:activityId',
        element: <FreeLayout><ActivityDetailPage /></FreeLayout>,
      },
      {
        path: '/public-timeline',
        element: <StandardLayout><PublicTimelinePage /></StandardLayout>,
      },
    ],
  },
]);

function App() {
  // メンテナンスモードの場合、MaintenancePageのみを表示
  if (maintenanceConfig.isUnderMaintenance && window.location.pathname !== '/maintenance') {
    return (
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {globalStyles}
          <MaintenancePage />
        </ThemeProvider>
      </HelmetProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID!}>
        <HelmetProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            {globalStyles}
            <UserProvider>
              <RouterProvider router={router} />
            </UserProvider>
          </ThemeProvider>
        </HelmetProvider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
}

export default App;
